import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Container,
    Grid,
    List,
    ListItemButton,
    ListItemText,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Project, ProjectStatus, PositionStatus } from '../../types/project';
import { projectService } from '../../services/projectService';
import { formatCurrency } from '../../utils/format';

const STATUS_LABELS: Record<ProjectStatus, string> = {
    [ProjectStatus.OPPORTUNITY]: 'Oportunidades',
    [ProjectStatus.PROPOSAL]: 'Propostas',
    [ProjectStatus.ACTIVE]: 'Em execução',
    [ProjectStatus.COMPLETED]: 'Concluídos',
    [ProjectStatus.CANCELLED]: 'Cancelados',
};

const Kpi: React.FC<{ label: string; value: React.ReactNode; loading: boolean }> = ({ label, value, loading }) => (
    <Card elevation={0} sx={{ height: '100%' }}>
        <CardContent>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            {loading ? <Skeleton width={70} height={42} /> : (
                <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.4rem', sm: '1.75rem', md: '2.125rem' }, overflowWrap: 'anywhere' }}>
                    {value}
                </Typography>
            )}
        </CardContent>
    </Card>
);

const ManagerDashboard: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        projectService.getManagerProjects()
            .then(setProjects)
            .catch(() => setError('Não foi possível carregar os projetos.'))
            .finally(() => setLoading(false));
    }, []);

    const byStatus = (status: ProjectStatus) => projects.filter((p) => p.status === status);
    const active = byStatus(ProjectStatus.ACTIVE);
    const proposals = byStatus(ProjectStatus.PROPOSAL);
    const activeRevenue = active.reduce((sum, p) => sum + (p.simulation?.total_revenue || 0), 0);

    // Posições da simulação selecionada dos projetos em execução
    const positions = active.flatMap((p) => p.simulation?.positions || []);
    const filled = positions.filter((pos) => pos.filled_by_user_id || pos.status === PositionStatus.FILLED).length;
    const open = positions.filter((pos) => pos.status === PositionStatus.OPEN || pos.status === PositionStatus.IN_PROGRESS).length;

    const recentSimulations = projects
        .flatMap((p) => (p.simulations || []).map((s) => ({ ...s, projectId: p.id, projectName: p.name })))
        .sort((a, b) => (b.id || 0) - (a.id || 0))
        .slice(0, 6);

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" gutterBottom>{t('manager.dashboard.title')}</Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Grid container spacing={3}>
                    <Grid item xs={6} md={3}><Kpi label="Oportunidades" value={byStatus(ProjectStatus.OPPORTUNITY).length} loading={loading} /></Grid>
                    <Grid item xs={6} md={3}><Kpi label="Propostas em aberto" value={proposals.length} loading={loading} /></Grid>
                    <Grid item xs={6} md={3}><Kpi label="Projetos em execução" value={active.length} loading={loading} /></Grid>
                    <Grid item xs={6} md={3}><Kpi label="Receita contratada" value={formatCurrency(activeRevenue)} loading={loading} /></Grid>

                    <Grid item xs={12} md={4}>
                        <Card elevation={0} sx={{ height: '100%' }}>
                            <CardHeader title={t('manager.dashboard.projectsSummary')} titleTypographyProps={{ variant: 'h6' }} />
                            <CardContent>
                                {loading ? <Skeleton variant="rounded" height={120} /> : projects.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">Nenhum projeto cadastrado.</Typography>
                                ) : (
                                    <List dense disablePadding>
                                        {(Object.keys(STATUS_LABELS) as ProjectStatus[]).map((status) => (
                                            <ListItemButton key={status} onClick={() => navigate('/gestor/projetos')}>
                                                <ListItemText primary={STATUS_LABELS[status]} />
                                                <Chip size="small" label={byStatus(status).length} />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card elevation={0} sx={{ height: '100%' }}>
                            <CardHeader title={t('manager.dashboard.teamMetrics')} titleTypographyProps={{ variant: 'h6' }}
                                subheader="Posições dos projetos em execução" />
                            <CardContent>
                                {loading ? <Skeleton variant="rounded" height={120} /> : positions.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">Nenhuma posição em projetos em execução.</Typography>
                                ) : (
                                    <List dense disablePadding>
                                        <ListItemText primary="Total de posições" secondary={positions.length} />
                                        <ListItemText primary="Preenchidas" secondary={filled} />
                                        <ListItemText primary="Vagas abertas / em seleção" secondary={open} />
                                        <ListItemText primary="Ainda não preenchidas" secondary={positions.length - filled} />
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card elevation={0} sx={{ height: '100%' }}>
                            <CardHeader title="Propostas aguardando o cliente" titleTypographyProps={{ variant: 'h6' }} />
                            <CardContent>
                                {loading ? <Skeleton variant="rounded" height={120} /> : proposals.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">Nenhuma proposta em aberto.</Typography>
                                ) : (
                                    <List dense disablePadding>
                                        {proposals.map((p) => (
                                            <ListItemButton key={p.id} onClick={() => navigate(`/gestor/projetos/${p.id}`)}>
                                                <ListItemText primary={p.name} secondary={p.client?.name} />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card elevation={0}>
                            <CardHeader title={t('manager.dashboard.recentSimulations')} titleTypographyProps={{ variant: 'h6' }} />
                            <CardContent>
                                {loading ? <Skeleton variant="rounded" height={120} /> : recentSimulations.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">Nenhuma simulação criada.</Typography>
                                ) : (
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Simulação</TableCell>
                                                <TableCell>Projeto</TableCell>
                                                <TableCell align="right">Receita</TableCell>
                                                <TableCell align="right">Margem</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {recentSimulations.map((s) => (
                                                <TableRow key={s.id} hover sx={{ cursor: 'pointer' }}
                                                    onClick={() => navigate(`/gestor/projetos/${s.projectId}/simulacao/${s.id}`)}>
                                                    <TableCell>{s.name}</TableCell>
                                                    <TableCell>{s.projectName}</TableCell>
                                                    <TableCell align="right">{formatCurrency(s.total_revenue)}</TableCell>
                                                    <TableCell align="right">{(s.profit_margin || 0).toFixed(1)}%</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default ManagerDashboard;
